import { memo, useEffect } from 'react'
import {
    readNextjsAudacitySignupFlow,
    readNextjsHqFlow,
    writeNextjsHqFlow,
} from '@core/session-dialogs'
import { useActions, useValues } from '@core/store'
import { authIntentStore, INTENT_TTL_MS } from '@features/auth'
import { checkoutStore } from '@features/checkout'
import { REG_PAYWALL_REQUEST, usePaywallDialog } from '@features/paywall'
import { useCurrentUser } from '@features/session'

/**
 * Opens the post-registration paywall once a registration completes (audio-web's `useShowRegPayWall`).
 * In the app layer for the same reason as `AuthIntentRunner`: `features/auth` must not reach into
 * `features/paywall`.
 */
export const RegPaywallRunner = memo(() => {
    const { id: userId } = useCurrentUser()
    const [regPaywall, intent, isCheckoutRedirectPending] = useValues([
        authIntentStore.props.getRegPaywall,
        authIntentStore.props.getIntent,
        checkoutStore.props.getIsRedirectPending,
    ])
    const { clearRegPaywall, touchIntent } = useActions({
        clearRegPaywall: authIntentStore.actions.clearRegPaywall,
        touchIntent: authIntentStore.actions.touchIntent,
    })
    const { open: openPaywall } = usePaywallDialog()

    useEffect(() => {
        if (userId === 0 || !regPaywall) {
            return
        }

        clearRegPaywall()

        if (isCheckoutRedirectPending || readNextjsAudacitySignupFlow()) {
            return
        }

        // A request left in localStorage must not surface on an unrelated later login.
        if (Date.now() - regPaywall.at > INTENT_TTL_MS) {
            return
        }

        // The intent's TTL should not run out while the user reads the paywall we put in front of it.
        touchIntent(Date.now())

        // What the guest was trying to do is what makes this the download flow — the same thing
        // audio-web reads off its pending `AUDIO_ID_TO_DOWNLOAD` / `COLLECTION_ID_TO_DOWNLOAD`.
        const isDownloadFlow = intent?.kind === 'download' || intent?.kind === 'download-collection'

        const isHqFlow = readNextjsHqFlow()

        writeNextjsHqFlow(false)

        openPaywall({
            ...REG_PAYWALL_REQUEST,
            flow: isDownloadFlow ? 'download' : undefined,
            omitsCheckoutTitle: isHqFlow,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, regPaywall])

    return null
})
